// Lecteur EXIF MINIMAL (NutriScan 10, § « Date depuis l'EXIF ») : la seule donnée qui nous
// intéresse est `DateTimeOriginal` (tag Exif 0x9003) — pas une bibliothèque EXIF complète, qui
// pèserait ~200 Ko de JS pour un seul champ (le budget mobile compte chaque kilo-octet, comme
// capture/compression.ts). On ne lit QUE les premiers octets du fichier (le segment APP1 qui porte
// l'EXIF est plafonné à 64 Ko par le format JPEG lui-même), jamais le fichier entier.
//
// Ne sait lire que du JPEG (marqueurs `FFE1`/APP1) : un HEIC stocke ses métadonnées dans un
// conteneur ISO BMFF entièrement différent, non traité ici — `dateDepuisFichier` retombe alors sur
// `lastModified` (limitation connue et non corrigée, au même titre que le décalage UTC/local de
// `journal/journalClient.ts` `aujourdhui()`).
//
// Fonctions pures, testées sans DOM (test/exifDate.test.ts) : `extraireDateExif` prend des octets
// bruts, `dateDepuisFichier` est le seul point qui touche `File`.

export type OrigineDate = 'exif' | 'lastModified' | 'maintenant';

export interface DateResolue {
  date: Date;
  origine: OrigineDate;
}

/** Nombre d'octets lus en tête de fichier — largement au-dessus du plafond de 64 Ko d'un segment
 *  APP1, pour couvrir aussi les cas où d'autres segments (miniature JFIF, ICC) précèdent l'EXIF. */
export const OCTETS_ENTETE_LUS = 131072;

const MARQUEUR_APP1 = 0xffe1;
const MARQUEUR_EOI = 0xffd9;
const MARQUEUR_SOS = 0xffda; // début du flux de données compressées : plus rien d'utile après
const TAG_DATE_TIME = 0x0132;
const TAG_EXIF_IFD_POINTER = 0x8769;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_DATE_TIME_DIGITIZED = 0x9004;
const TYPE_ASCII = 2;

interface EntreeIfd {
  tag: number;
  type: number;
  count: number;
  /** Position ABSOLUE (dans le buffer entier) des 4 octets de valeur/offset de cette entrée. */
  posValeur: number;
}

class LecteurTiff {
  constructor(
    private readonly vue: DataView,
    private readonly littleEndian: boolean,
  ) {}
  u16(offset: number): number {
    return this.vue.getUint16(offset, this.littleEndian);
  }
  u32(offset: number): number {
    return this.vue.getUint32(offset, this.littleEndian);
  }
}

/** Lit les entrées d'un IFD (Image File Directory) — TIFF/EXIF partagent le même format de table. */
function analyserIfd(lecteur: LecteurTiff, offsetIfd: number, limite: number): EntreeIfd[] | null {
  if (offsetIfd < 0 || offsetIfd + 2 > limite) return null;
  const nombre = lecteur.u16(offsetIfd);
  const entrees: EntreeIfd[] = [];
  for (let i = 0; i < nombre; i++) {
    const pos = offsetIfd + 2 + i * 12;
    if (pos + 12 > limite) break; // IFD tronqué par notre fenêtre de lecture : on garde ce qu'on a
    entrees.push({ tag: lecteur.u16(pos), type: lecteur.u16(pos + 2), count: lecteur.u32(pos + 4), posValeur: pos + 8 });
  }
  return entrees;
}

/** Chaîne ASCII d'une entrée : inline dans les 4 octets si `count <= 4`, sinon à `tiffStart + offset`. */
function lireAscii(lecteur: LecteurTiff, bytes: Uint8Array, tiffStart: number, entree: EntreeIfd, limite: number): string | null {
  if (entree.type !== TYPE_ASCII || entree.count <= 0) return null;
  const debut = entree.count <= 4 ? entree.posValeur : tiffStart + lecteur.u32(entree.posValeur);
  if (debut < 0 || debut + entree.count > limite) return null;
  let s = '';
  for (let i = 0; i < entree.count; i++) {
    const octet = bytes[debut + i]!;
    if (octet === 0) break; // chaîne EXIF terminée par NUL
    s += String.fromCharCode(octet);
  }
  return s;
}

/** `"2024:01:15 12:30:00"` -> `Date` locale. EXIF ne porte pas de fuseau dans ce tag : traité comme
 *  l'heure locale de l'appareil qui a pris la photo, faute de mieux. */
function parseDateExifTexte(texte: string): Date | null {
  const correspondance = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(texte);
  if (!correspondance) return null;
  const [, annee, mois, jour, heure, minute, seconde] = correspondance;
  const date = new Date(Number(annee), Number(mois) - 1, Number(jour), Number(heure), Number(minute), Number(seconde));
  return Number.isNaN(date.getTime()) ? null : date;
}

function extraireDateDepuisTiff(vue: DataView, bytes: Uint8Array, tiffStart: number): Date | null {
  if (tiffStart + 8 > bytes.length) return null;
  const marqueurOrdre = vue.getUint16(tiffStart, false);
  let littleEndian: boolean;
  if (marqueurOrdre === 0x4949) littleEndian = true; // 'II'
  else if (marqueurOrdre === 0x4d4d) littleEndian = false; // 'MM'
  else return null;

  const lecteur = new LecteurTiff(vue, littleEndian);
  if (lecteur.u16(tiffStart + 2) !== 0x002a) return null; // constante TIFF (42)
  const limite = bytes.length;
  const ifd0 = analyserIfd(lecteur, tiffStart + lecteur.u32(tiffStart + 4), limite);
  if (!ifd0) return null;

  // 1. Sous-IFD Exif : DateTimeOriginal (prise de vue), puis DateTimeDigitized — les deux plus
  //    fiables, à privilégier sur le DateTime générique de l'IFD0 (souvent une date de modification).
  const pointeurExif = ifd0.find((e) => e.tag === TAG_EXIF_IFD_POINTER);
  if (pointeurExif) {
    const exifIfd = analyserIfd(lecteur, tiffStart + lecteur.u32(pointeurExif.posValeur), limite);
    if (exifIfd) {
      for (const tag of [TAG_DATE_TIME_ORIGINAL, TAG_DATE_TIME_DIGITIZED]) {
        const entree = exifIfd.find((e) => e.tag === tag);
        const texte = entree ? lireAscii(lecteur, bytes, tiffStart, entree, limite) : null;
        const date = texte ? parseDateExifTexte(texte) : null;
        if (date) return date;
      }
    }
  }

  // 2. Repli : IFD0.DateTime — souvent la date de dernière modification du fichier, pas de la prise
  //    de vue, mais toujours mieux qu'un `lastModified` de transfert (partage, copie sur un autre
  //    support) qui n'a plus aucun rapport avec le moment du repas.
  const entreeIfd0 = ifd0.find((e) => e.tag === TAG_DATE_TIME);
  const texteIfd0 = entreeIfd0 ? lireAscii(lecteur, bytes, tiffStart, entreeIfd0, limite) : null;
  return texteIfd0 ? parseDateExifTexte(texteIfd0) : null;
}

/**
 * Cherche `DateTimeOriginal` (ou son repli) dans les octets de tête d'un JPEG. `null` si le fichier
 * n'est pas un JPEG, n'a pas de segment APP1/EXIF, ou si le tag est absent — jamais une exception :
 * l'appelant (`dateDepuisFichier`) retombe alors sur `lastModified`.
 */
export function extraireDateExif(bytes: Uint8Array): Date | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null; // pas de SOI JPEG
  const vue = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 2;
  while (pos + 4 <= bytes.length) {
    if (bytes[pos] !== 0xff) break; // flux corrompu ou fin des marqueurs : rien de plus à lire
    const marqueur = vue.getUint16(pos, false);
    if (marqueur === MARQUEUR_EOI || marqueur === MARQUEUR_SOS) break;
    const tailleSegment = vue.getUint16(pos + 2, false);
    if (tailleSegment < 2) break; // segment invalide, on ne boucle pas indéfiniment dessus
    if (marqueur === MARQUEUR_APP1) {
      const debutPayload = pos + 4;
      const estExif =
        debutPayload + 6 <= bytes.length &&
        bytes[debutPayload] === 0x45 && // E
        bytes[debutPayload + 1] === 0x78 && // x
        bytes[debutPayload + 2] === 0x69 && // i
        bytes[debutPayload + 3] === 0x66 && // f
        bytes[debutPayload + 4] === 0x00 &&
        bytes[debutPayload + 5] === 0x00;
      if (estExif) {
        const date = extraireDateDepuisTiff(vue, bytes, debutPayload + 6);
        if (date) return date;
      }
    }
    pos += 2 + tailleSegment;
  }
  return null;
}

/**
 * Date à retenir pour une photo IMPORTÉE (NutriScan 10) : `DateTimeOriginal` (EXIF) en premier,
 * `File.lastModified` en repli, l'instant présent en dernier recours — dans cet ordre, jamais
 * l'inverse (périmètre § « Date depuis l'EXIF »). Ne lève jamais : une lecture EXIF impossible ne
 * doit pas empêcher l'import, juste dater la photo moins précisément.
 */
export async function dateDepuisFichier(fichier: File): Promise<DateResolue> {
  try {
    const entete = new Uint8Array(await fichier.slice(0, OCTETS_ENTETE_LUS).arrayBuffer());
    const dateExif = extraireDateExif(entete);
    if (dateExif) return { date: dateExif, origine: 'exif' };
  } catch {
    // Lecture impossible (fichier verrouillé, décodeur absent) : repli sans bloquer l'import.
  }
  if (fichier.lastModified > 0) return { date: new Date(fichier.lastModified), origine: 'lastModified' };
  return { date: new Date(), origine: 'maintenant' };
}
